import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { TripState } from '../../types/trip.store.type';
import { IconArrowRight, IconArrowRightNtf, IconCalendarNtf, IconCheckMark, IconClockNtf, IconPoolNtf } from '../../assets/icons';
import './StayPanel.scss';
import { SharedUtils, TranslationUtils } from '../../utils';
import { FormattedDate } from 'react-intl';
import { LateCheckOutModal } from '..';
import { AppRoutes } from '../../routes';
import { useHistory } from 'react-router-dom';
import { AdjustmentConfig, Splits } from '../../types/split.type';
import segmentService from '../../services/segment/segmentService';
import { PoolHeatModal } from '../PoolHeatModal/PoolHeatModal';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../services/optimizely/optimizelyHelper';

export enum CheckDateTypes {
    CHECK_IN = 'CHECK_IN',
    CHECK_OUT = 'CHECK_OUT',
}

export const StayPanel: React.FC = () => {
    const stay = useSelector((store: { trip: TripState }) => store.trip.stay);

    const [isShowingLateCheckoutModal, setIsShowLateCheckoutModal] = useState(false);
    const [isShowingPoolHeatModal, setIsShowPoolHeatModal] = useState(false);

    const history = useHistory();

    const reservation = useSelector((store: { trip: TripState }) => store.trip.reservation);
    const {
        isEnabled: isPoolHeatEnable,
        config: poolHeatConfig,
        clientReady: poolHeatClientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_POOL_HEAT, true);

    if (!stay) {
        return null;
    }

    interface NotificationOption {
        icon: any;
        message: string;
        onNotificationClick: () => void;
        next: boolean;
    }

    const getNotificationOption = (): NotificationOption => {
        if (stay.late_check_out_purchased) {
            return {
                icon: <IconCheckMark />,
                message: stay.late_check_out_warning,
                onNotificationClick: () => null,
                next: false,
            };
        }

        if (stay.late_check_out_available) {
            return {
                icon: <IconClockNtf />,
                message: stay.late_check_out_warning,
                onNotificationClick: toggleLateCheckoutModal,
                next: true,
            };
        }

        if (stay.early_check_in && moment().isSameOrBefore(stay.check_out_date_time, 'date')) {
            return {
                icon: <IconClockNtf />,
                message: stay.early_check_in_warning,
                onNotificationClick: () => null,
                next: false,
            };
        }

        return { icon: null, message: '', onNotificationClick: () => null, next: false };
    };

    const extendStayNotificationOptions = (): NotificationOption => {
        if (stay.extend_stay_eligible && stay.extend_stay_available) {
            return {
                icon: <IconCalendarNtf />,
                message: stay.extend_stay_overview,
                onNotificationClick: redirectToToExtendStayView,
                next: true,
            };
        }
        return { icon: null, message: '', onNotificationClick: () => null, next: false };
    };

    const getPoolHeatNotificationOption = (): NotificationOption => {
        const isPoolHeatEnabled = isFeatureAvailableForChannel(reservation, poolHeatClientReady, poolHeatConfig, isPoolHeatEnable);
        if (!isPoolHeatEnabled) {
            return { icon: null, message: '', onNotificationClick: () => null, next: false };
        }

        if (stay.pool_heat_contact_gx) {
            const formattedPhoneNumber = SharedUtils.getFormattedTollFreePhoneNumber();
            const messageWithPhoneNumber = stay.pool_heat_message.replace('{{phone}}', formattedPhoneNumber);

            return {
                icon: <IconPoolNtf />,
                message: messageWithPhoneNumber,
                onNotificationClick: () => null,
                next: false,
            };
        }

        if (stay.pool_heat_available) {
            return {
                icon: <IconPoolNtf />,
                message: stay.pool_heat_message,
                onNotificationClick: togglePoolHeatModal,
                next: true,
            };
        }

        return { icon: null, message: '', onNotificationClick: () => null, next: false };
    };

    const renderCheckDate = (checkType: CheckDateTypes) => {
        const title: string = checkType === CheckDateTypes.CHECK_IN ? 'CHECK IN' : 'CHECK OUT';
        const date = checkType === CheckDateTypes.CHECK_IN ? stay.check_in_date_time : stay.check_out_date_time;
        let time;

        if (checkType === CheckDateTypes.CHECK_IN) {
            time = moment(`${stay.check_in_date} ${stay.check_in_time}`);
        } else {
            const checkOutTime = stay.late_check_out_purchased ? stay.late_check_out_time : stay.check_out_time;
            time = moment(`${stay.check_out_date} ${checkOutTime}`);
        }

        return (
            <div className="stay-panel-check-date">
                <div tabIndex={0} aria-label={`${title}`} className="heading-caps-small head-title">
                    {title}
                </div>
                <div className={'h5'} tabIndex={0}>
                    <h5 className={'body-xl'}>
                        <FormattedDate value={new Date(date)} month="short" day="numeric" weekday="short" year="numeric" />
                    </h5>
                </div>
                <span className="body-regular" tabIndex={0} aria-label={`${moment(time).format('LT')}`}>
                    {moment(time).format('LT').toLowerCase().replace(/(.)m/g, '$1.m.')}
                </span>
            </div>
        );
    };

    const toggleLateCheckoutModal = () => {
        setIsShowLateCheckoutModal(() => !isShowingLateCheckoutModal);
    };

    const togglePoolHeatModal = () => {
        setIsShowPoolHeatModal(() => !isShowingPoolHeatModal);
    };

    const redirectToToExtendStayView = () => {
        segmentService.trackEvent('Trip Portal Extend Stay Banner', {
            action: 'click',
            page: AppRoutes.RESERVATION,
        });
        history.push(AppRoutes.EXTENDED_STAY);
    };

    if (!stay) {
        return null;
    }

    const checkIn = moment(new Date(stay.check_in_date_time));
    const checkOut = moment(new Date(stay.check_out_date_time));

    // we change the .diff because in this calculation it does the rounding with the second decimal down
    const nights = Math.round(checkOut.diff(checkIn, 'hours') / 24);

    const notificationsArray: NotificationOption[] = [];

    notificationsArray.push(extendStayNotificationOptions());
    notificationsArray.push(getNotificationOption());
    notificationsArray.push(getPoolHeatNotificationOption());

    return (
        <React.Fragment>
            <div className="panel stay-panel">
                <Row>
                    <Col md={3} className="d-none d-md-block">
                        <span tabIndex={0} aria-label={`${nights} ${TranslationUtils.get('nights')}`}>
                            <div className="nights">
                                <h3 className="heading-med-light h3 info">{nights}</h3>
                                <p className="body-tiny info-subtitle">{TranslationUtils.get('nights')}</p>
                            </div>
                        </span>
                    </Col>

                    <Col xs={5} sm={5} md={4}>
                        <span>{renderCheckDate(CheckDateTypes.CHECK_IN)}</span>
                    </Col>
                    <Col xs={2} sm={2} md={1}>
                        <div className="divider-arrow">
                            <IconArrowRight />
                        </div>
                    </Col>
                    <Col xs={5} sm={5} md={4}>
                        <span>{renderCheckDate(CheckDateTypes.CHECK_OUT)}</span>
                    </Col>
                </Row>

                {notificationsArray.map(
                    (ntf) =>
                        !!(ntf.icon && ntf.message) && (
                            <Row>
                                <Col>
                                    <div
                                        className={`stay-panel-notification ${ntf.next && 'stay-panel-pointer'}`}
                                        onClick={ntf.onNotificationClick}
                                    >
                                        <div className={'stay-panel-notification-text'}>
                                            <div className={`stay-panel-notification-icon`}>{ntf.icon}</div>
                                            <span className="stay-panel-notification-message"> {ntf.message}</span>
                                        </div>
                                        <div className="stay-panel-notification-icon ">{ntf.next && <IconArrowRightNtf />}</div>
                                    </div>
                                </Col>
                            </Row>
                        )
                )}
            </div>

            <LateCheckOutModal
                show={isShowingLateCheckoutModal}
                handleClose={toggleLateCheckoutModal}
                message={stay.late_check_out_overview}
            />

            <PoolHeatModal show={isShowingPoolHeatModal} handleClose={togglePoolHeatModal} />
        </React.Fragment>
    );
};
